import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { setFlowMethodConditionList } from 'Actions/flowMethodList';
import { invalidateFlowVariables } from 'Actions/flowVariables';
import {
  Box,
  Grid,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Stack,
  TextField,
  IconButton,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { CustomBlackSwitch } from 'Shared/CustomBlackSwitch';
import { CloseIcon } from 'Shared/Icons';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { AlertDialog } from 'Shared/AlertDialog';
import http from 'Utils/http';
import { useCreateUrl } from 'Hooks/useCreateUrl';

export const FlowRuleForm = ({
                                 index,
                                 updateItems,
                                 transformation_id,
                                 condition_id,
                                 preCondition,
                                 setPreCondition,
                                 switchType = null,
                                 variables = [],
                                 variablesLoaded = false,
                             }) => {
    const dispatch = useDispatch();
    const { createUrl } = useCreateUrl();
    const { enqueueSnackbar } = useSnackbar();

    const modalText = 'Are you sure you want to delete condition?';

    // Get raw data from Redux state
    const fieldListRaw = useSelector((state) => state.flowMethodList.fieldList);
    const conditionListRaw = useSelector((state) => state.flowMethodList?.conditionList);
    const operatorsRaw = useSelector((state) => state.operators.operators);
    const flowId = useSelector((state) => state.flow?.flow?.id);

    // Memoize filtered selectors to prevent unnecessary re-renders
    const fieldList = useMemo(() => 
        fieldListRaw?.filter((item) => item.name !== 'hidden_field_in_input_port'), 
        [fieldListRaw]
    );
    
    const conditions = useMemo(() => 
        conditionListRaw?.filter((item) => item.transformation_id === transformation_id) ?? [], 
        [conditionListRaw, transformation_id]
    );
    
    const operatorsList = useMemo(() => operatorsRaw, [operatorsRaw]);
    const operatorsListSingle = useMemo(() => 
        operatorsRaw?.logical?.filter((i) => i.single) ?? [], 
        [operatorsRaw]
    );

    // Memoize other selectors
    const memoizedFieldList = useMemo(() => fieldList, [fieldList]);
    const memoizedConditions = useMemo(() => conditions, [conditions]);
    const memoizedOperatorsList = useMemo(() => operatorsList, [operatorsList]);
    const memoizedOperatorsListSingle = useMemo(() => operatorsListSingle, [operatorsListSingle]);
    const memoizedVariables = useMemo(() => variables ?? [], [variables]);
    const canUseVariables = memoizedVariables.length > 0;
    const variablesUnavailable = variablesLoaded && !canUseVariables;

    // Memoize default condition to prevent unnecessary re-renders
    const defaultCondition = useMemo(() => {
        if (!preCondition) {
            const currentCondition = memoizedConditions[index] ?? {};
            const dataSelectOperandB =
                currentCondition?.constant ? 'CONSTANT' :
                    currentCondition?.condition_id ? 'PREVCONDITION' :
                        (currentCondition?.operand_b_variable ? 'VARIABLE' : 'FIELD');

            const selectOperandA = currentCondition?.operand_a_variable
                ? 'VARIABLE'
                : 'FIELD';

            const operandAValue = currentCondition?.operand_a_variable ?? currentCondition?.operand_a ?? '';
            const operandBValue = currentCondition?.operand_b_variable ?? currentCondition?.operand_b ?? '';

            return {
                select_operand_a: selectOperandA,
                operand_a: operandAValue,
                select_operand_b: dataSelectOperandB,
                operand_b: dataSelectOperandB === 'FIELD' ? operandBValue : '',
                operand_b_variable: dataSelectOperandB === 'VARIABLE' ? operandBValue : '',
                operation_id: currentCondition?.operation_id || 'null',
                constant: currentCondition?.constant || ''
            };
        }

        return {
            select_operand_a: 'FIELD',
            select_operand_b: 'FIELD',
            operand_a: '',
            operation_id: 'null',
            operand_b: '',
            operand_b_variable: '',
            constant: ''
        };
    }, [preCondition, memoizedConditions, index]);

    // Memoize findOperation result
    const findOperation = useMemo(() => 
        !!defaultCondition?.operation_id && memoizedOperatorsListSingle.some((item) => item.id === defaultCondition.operation_id),
        [defaultCondition, memoizedOperatorsListSingle]
    );

    // Memoize initial active state
    const initialActiveState = useMemo(() => {
        if (!defaultCondition.operand_a) return null;
        return defaultCondition.operand_a;
    }, [defaultCondition.operand_a]);

    const [switchValue, setSwitchValue] = useState(switchType !== 'and');
    const [needDisable, setNeedDisable] = useState(findOperation);
    const [isActiveModal, setIsActiveModal] = useState(false);
    const [isDropdown, setIsDropdown] = useState(false);
    const [visibleFieldsList, setVisibleFieldsList] = useState(defaultCondition?.select_operand_b);
    const [isActive] = useState(() => initialActiveState);
    const onClose = () => setIsActiveModal(false);
    const onAgree = () => {
        setIsActiveModal(false);
        deleteRuleHandler();
    };
    const invalidateVariables = () => {
        if (flowId) {
            dispatch(invalidateFlowVariables(flowId));
        }
    };

    const deleteRuleHandler = () => {
        if (preCondition) {
            enqueueSnackbar('You yet not save condition!', {
                action: CustomSnackbar,
                variant: 'warning'
            });
            setPreCondition([]);
            return;
        }

        if (!memoizedConditions.length) {
            enqueueSnackbar('You yet not save condition!', {
                action: CustomSnackbar,
                variant: 'warning'
            });
            return;
        }

        dispatch(setLoading(true));
        http.delete(createUrl(`/condition/${condition_id}/delete`)).then((res) => {
            dispatch(setFlowMethodConditionList(res.data.flowMethod.conditions));
            enqueueSnackbar('Condition has been deleted', {
                action: CustomSnackbar,
                variant: 'success'
            });
            invalidateVariables();
        }).catch((err) => {
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        }).finally(() => {
            dispatch(setLoading(false));
        });
    };

    const {
        register,
        watch,
        control,
        setValue,
        getValues,
    } = useForm({
        defaultValues: {
            select_operand_a: defaultCondition.select_operand_a,
            operand_a: defaultCondition.operand_a,
            operation_id: defaultCondition.operation_id,
            select_operand_b: defaultCondition.select_operand_b,
            operand_b: defaultCondition.operand_b,
            operand_b_variable: defaultCondition.operand_b_variable,
            constant: defaultCondition.constant
        },
        mode: 'onChange',
    });

    const selectOperandAValue = watch('select_operand_a');
    const selectOperandBValue = watch('select_operand_b');
    const isVariableOperandA = selectOperandAValue === 'VARIABLE';

    useEffect(() => {
        if (selectOperandAValue === 'VARIABLE' && variablesUnavailable) {
            setValue('select_operand_a', 'FIELD');
        }
    }, [selectOperandAValue, variablesUnavailable, setValue]);

    useEffect(() => {
        if (selectOperandBValue === 'VARIABLE' && variablesUnavailable) {
            setValue('select_operand_b', 'FIELD');
            setVisibleFieldsList('FIELD');
        }
    }, [selectOperandBValue, variablesUnavailable, setValue]);

    useEffect(() => {
        if (selectOperandAValue === 'VARIABLE') {
            if (canUseVariables) {
                const currentValue = getValues('operand_a');
                if (!memoizedVariables.includes(currentValue)) {
                    setValue('operand_a', memoizedVariables[0] ?? '');
                }
            } else if (variablesUnavailable) {
                setValue('operand_a', '');
            }
        }

        if (selectOperandAValue === 'FIELD') {
            if (memoizedFieldList?.length) {
                const currentValue = getValues('operand_a');
                const hasField = memoizedFieldList.some(({ id }) => `${id}` === `${currentValue}`);
                if (!hasField) {
                    setValue('operand_a', memoizedFieldList[0]?.id ?? '');
                }
            } else {
                setValue('operand_a', '');
            }
        }
    }, [selectOperandAValue, canUseVariables, variablesUnavailable, memoizedVariables, memoizedFieldList, setValue, getValues]);

    useEffect(() => {
        if (selectOperandBValue === 'VARIABLE') {
            if (canUseVariables) {
                const currentValue = getValues('operand_b_variable');
                if (!memoizedVariables.includes(currentValue)) {
                    setValue('operand_b_variable', memoizedVariables[0] ?? '');
                }
            } else if (variablesUnavailable) {
                setValue('operand_b_variable', '');
            }
        }

        if (selectOperandBValue === 'FIELD') {
            if (memoizedFieldList?.length) {
                const currentValue = getValues('operand_b');
                const hasField = memoizedFieldList.some(({ id }) => `${id}` === `${currentValue}`);
                if (!hasField) {
                    setValue('operand_b', memoizedFieldList[0]?.id ?? '');
                }
            } else {
                setValue('operand_b', '');
            }
        }
    }, [selectOperandBValue, canUseVariables, variablesUnavailable, memoizedVariables, memoizedFieldList, setValue, getValues]);

    const handleError = useCallback((err) => {
        dispatch(setLoading(false));
        enqueueSnackbar(`${err.response.data.message}`, {
            action: CustomSnackbar,
            variant: 'error',
        });
    }, [dispatch, enqueueSnackbar]);

    const handleSwitchValue = useCallback(() => {
        setSwitchValue(!switchValue);
    }, [setSwitchValue, switchValue]);

    const saveRule = (needDelete = false) => {
        if (needDelete) {
            deleteRule();
            return;
        }

        const type = getType();
        const operandB = getOperandB();

        const selectOperandA = getValues('select_operand_a');
        const operandAValue = getValues('operand_a');

        const paramsForAddCondition = {
            type,
            transformation_id: transformation_id,
            select_operand_a: selectOperandA,
            select_operand_b: getValues('select_operand_b'),
            operation_id: getValues('operation_id'),
            ...operandB,
        };

        if (selectOperandA === 'VARIABLE') {
            paramsForAddCondition.operand_a = null;
            paramsForAddCondition.operand_a_variable = operandAValue || null;
        } else {
            paramsForAddCondition.operand_a = operandAValue !== undefined && operandAValue !== null && operandAValue !== ''
                ? Number(operandAValue)
                : null;
            paramsForAddCondition.operand_a_variable = null;
        }

        if (visibleFieldsList === 'PREVCONDITION') {
            paramsForAddCondition.condition_id = memoizedConditions[index - 1]?.id;
        }

        if (!memoizedConditions.length || preCondition) {
            dispatchWithLoading(() =>
                http.post(createUrl('/condition/add'), paramsForAddCondition).then((res) => {
                    dispatch(setFlowMethodConditionList(res.data.flowMethod.conditions));
                    setPreCondition([]);
                    enqueueSnackbar('Rule has been saved', {
                        action: CustomSnackbar,
                        variant: 'success',
                    });
                    invalidateVariables();
                }).catch(handleError)
            );
        } else {
            updateRule(paramsForAddCondition);
        }
    };

    const deleteRule = () => {
        const updId = memoizedConditions[index]?.id;
        if (!updId || getValues('operation_id') === 'null') {
            return;
        }

        dispatchWithLoading(() =>
            http.delete(createUrl(`/condition/${updId}/delete`)).then((res) => {
                dispatch(setFlowMethodConditionList(res.data.flowMethod.conditions));
                setPreCondition([]);
                invalidateVariables();
            }).catch(handleError)
        );
    };

    const updateRule = (params) => {
        const updId = memoizedConditions[index]?.id;
        if (!updId) {
            return;
        }

        dispatchWithLoading(() =>
            http.post(createUrl(`/condition/${updId}/update`), params).then(() => {
                setPreCondition([]);
                updateItems();
                enqueueSnackbar('Rule has been saved', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                invalidateVariables();
            }).catch(handleError)
        );
    };

    const getType = () => {
        if (index === 0) {
            return 'single';
        }
        return !switchValue ? 'and' : 'or';
    };

    const getOperandB = () => {
        if (!needDisable) {
            if (visibleFieldsList === 'CONSTANT') {
                return {
                    constant: getValues('constant'),
                    operand_b: null,
                    operand_b_variable: null,
                };
            }
            if (visibleFieldsList === 'FIELD') {
                const operandBValue = getValues('operand_b');
                return {
                    operand_b: operandBValue !== undefined && operandBValue !== null && operandBValue !== ''
                        ? Number(operandBValue)
                        : null,
                    constant: null,
                    operand_b_variable: null,
                };
            }
            if (visibleFieldsList === 'VARIABLE') {
                const operandBVariableValue = getValues('operand_b_variable');
                return {
                    operand_b: null,
                    constant: null,
                    operand_b_variable: operandBVariableValue || null,
                };
            }
        }
        return {
            operand_b: null,
            operand_b_variable: null,
            constant: null,
        };
    };

    const dispatchWithLoading = (action) => {
        dispatch(setLoading(true));
        action().finally(() => dispatch(setLoading(false)));
    };

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (values[name]) {
                if (name === 'operation_id') {
                    setNeedDisable(memoizedOperatorsListSingle.some(item => item.id === values.operation_id));
                }

                if (name === 'select_operand_a') {
                    if (values[name] === 'FIELD') {
                        if (memoizedFieldList?.length) {
                            setValue('operand_a', memoizedFieldList[0]?.id ?? '');
                        } else {
                            setValue('operand_a', '');
                        }
                    }

                    if (values[name] === 'VARIABLE') {
                        if (memoizedVariables.length) {
                            setValue('operand_a', memoizedVariables[0]);
                        } else {
                            setValue('operand_a', '');
                        }
                    }
                }

                const operandB = values.select_operand_b;

                if (name === 'select_operand_b') {
                    switch (operandB) {
                        case 'FIELD':
                            if (memoizedFieldList?.length) {
                                setValue('operand_b', memoizedFieldList[0]?.id ?? '');
                            } else {
                                setValue('operand_b', '');
                            }
                            setValue('operand_b_variable', '');
                            setValue('constant', '');
                            break;
                        case 'VARIABLE':
                            if (memoizedVariables.length) {
                                setValue('operand_b_variable', memoizedVariables[0]);
                            } else {
                                setValue('operand_b_variable', '');
                            }
                            setValue('operand_b', '');
                            setValue('constant', '');
                            break;
                        case 'CONSTANT':
                            setValue('operand_b', '');
                            setValue('operand_b_variable', '');
                            break;
                        case 'PREVCONDITION':
                            setValue('operand_b', '');
                            setValue('operand_b_variable', '');
                            setValue('constant', '');
                            break;
                        default:
                    }
                }

                switch (operandB) {
                    case 'CONSTANT':
                        setVisibleFieldsList('CONSTANT');
                        break;
                    case 'FIELD':
                        setVisibleFieldsList('FIELD');
                        break;
                    case 'PREVCONDITION':
                        setVisibleFieldsList('PREVCONDITION');
                        break;
                    case 'VARIABLE':
                        setVisibleFieldsList('VARIABLE');
                        break;
                    default:
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, memoizedOperatorsListSingle, memoizedFieldList, memoizedVariables, setValue, setNeedDisable]);

    return (
        <>
            <AlertDialog visible={isActiveModal} onClose={onClose} onAgree={onAgree} text={modalText}/>
            <Box
                component="form"
                sx={{
                    marginBottom: '10px',
                    padding: '13px 12px 12px 12px',
                    background: 'rgba(65, 77, 101, 0.05)',
                    borderRadius: '6px',
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', padding: '0', cursor: 'pointer'}}
                     onClick={() => {
                         setIsDropdown(!isDropdown)
                     }}>
                    <Typography sx={{
                        marginRight: '16px',
                        fontWeight: '700',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'rgba(65, 77, 101, 0.85)',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <span>#{index + 1} Rule</span>
                        {isActive ? <Box sx={{
                            display: 'flex',
                            marginLeft: '10px',
                            width: '10px',
                            height: '10px',
                            background: '#1BBC6F',
                            borderRadius: '5px'
                        }}></Box> : null}
                    </Typography>

                    {index !== 0 && (
                        <Stack direction="row" alignItems="center">
                            <Typography color="#949DB0">and</Typography>
                            <CustomBlackSwitch checked={switchValue} onChange={handleSwitchValue}/>
                            <Typography color="#949DB0">or</Typography>
                        </Stack>
                    )}

                    <Box sx={{ marginLeft: 'auto' }}>
                        {(index !== 0) && (
                            <IconButton
                                type="button"
                                onClick={() => {
                                    setIsActiveModal(true);
                                }}>
                                <CloseIcon/>
                            </IconButton>
                        )}
                        <IconButton>
                            <SettingsIcon
                                type="button"
                                sx={{ color: '#949DB0' }}/>
                        </IconButton>
                    </Box>
                </Box>

                {isDropdown && (<Grid container columnSpacing={'10px'}>
                    <Grid item xs={4}>
                        <Typography
                            sx={{
                                lineHeight: '17px',
                                marginBottom: '10px',
                                fontWeight: '500',
                                color: '#949DB0',
                            }}
                        >
                            Select Operands
                        </Typography>
                        <FormControl sx={{ width: '100%', marginBottom: '12px' }}>
                            <InputLabel>1st Operand</InputLabel>
                            <Controller
                                id="select_operand_a"
                                name="select_operand_a"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="1st Operand"
                                    >
                                        <MenuItem value="FIELD">Field</MenuItem>
                                        {canUseVariables && (
                                            <MenuItem value="VARIABLE">Variable</MenuItem>
                                        )}
                                    </Select>
                                )}
                            />
                        </FormControl>

                        <FormControl sx={{ width: '100%', marginBottom: '12px' }}>
                            <InputLabel>1st Operand Value</InputLabel>
                            <Controller
                                id="operand_a"
                                name="operand_a"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select {...field} label="1st Operand Value">
                                        {isVariableOperandA
                                            ? memoizedVariables.map((variable) => (
                                                <MenuItem key={`variable_${variable}`} value={variable}>
                                                    {variable}
                                                </MenuItem>
                                            ))
                                            : memoizedFieldList?.map(({ id, name }) => (
                                                <MenuItem key={`${id}_1st_field_id`} value={id}>
                                                    {name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                        <FormControl
                            sx={{
                                width: '100%',
                                marginTop: '27px',
                                marginBottom: '12px',
                            }}
                        >
                            <InputLabel>Condition</InputLabel>
                            <Controller
                                id="operation_id"
                                name="operation_id"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="Condition"
                                    >
                                        <MenuItem value={'null'}>No condition</MenuItem>
                                        {memoizedOperatorsList?.logical.map(({ id, operation }) => (
                                            <MenuItem key={`${id}_logical_no_condition`} value={id}>
                                                {operation}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                        <Typography
                            sx={{
                                lineHeight: '17px',
                                marginBottom: '10px',
                                fontWeight: '500',
                                color: '#949DB0',
                            }}
                        >
                            Select Operands
                        </Typography>

                        <FormControl
                            sx={{
                                width: '100%',
                                marginBottom: '12px',
                            }}
                        >
                            <InputLabel>2nd Operand</InputLabel>
                            <Controller
                                id="select_operand_b"
                                name="select_operand_b"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        label="2nd Operand"
                                        disabled={needDisable}
                                    >
                                        <MenuItem value={'CONSTANT'}>Constant</MenuItem>
                                        <MenuItem value={'FIELD'}>Field</MenuItem>
                                        {canUseVariables && (
                                            <MenuItem value={'VARIABLE'}>Variable</MenuItem>
                                        )}
                                        {index > 0 && (
                                            <MenuItem value={'PREVCONDITION'}>Previous condition</MenuItem>
                                        )}
                                    </Select>
                                )}
                            />
                        </FormControl>

                        {visibleFieldsList === 'CONSTANT' && (
                            <FormControl
                                sx={{
                                    width: '100%',
                                    marginBottom: '12px',
                                }}
                            >
                                <TextField
                                    label="Constant"
                                    disabled={needDisable}
                                    {...register('constant', {
                                        required: true,
                                    })}
                                />
                            </FormControl>
                        )}

                        {visibleFieldsList === 'FIELD' && (
                            <FormControl
                                sx={{
                                    width: '100%',
                                    marginBottom: '12px',
                                }}
                            >
                                <InputLabel>2nd Operand Value</InputLabel>
                                <Controller
                                    id="operand_b"
                                    name="operand_b"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            label="2nd Operand Value"
                                            disabled={needDisable}
                                        >
                                            {memoizedFieldList?.map(({ id, name }) => (
                                                <MenuItem key={`${id}_2nd_field_id`} value={id}>
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        )}

                        {visibleFieldsList === 'VARIABLE' && (
                            <FormControl
                                sx={{
                                    width: '100%',
                                    marginBottom: '12px',
                                }}
                            >
                                <InputLabel>2nd Operand Value</InputLabel>
                                <Controller
                                    id="operand_b_variable"
                                    name="operand_b_variable"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            label="2nd Operand Value"
                                            disabled={needDisable}
                                        >
                                            {memoizedVariables.map((variable) => (
                                                <MenuItem key={`variable_${variable}_operand_b`} value={variable}>
                                                    {variable}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        )}

                        {visibleFieldsList === 'PREVCONDITION' && (
                            <FormControl
                                sx={{
                                    width: '100%',
                                    marginBottom: '12px',
                                }}
                            >
                                <TextField
                                    disabled
                                    label={`Prev Id: ${memoizedConditions[index - 1]?.id}`}
                                    {...register('condition_id', {
                                        required: true,
                                    })}
                                />
                            </FormControl>
                        )}

                        <Button
                            variant="contained"
                            sx={{
                                width: '100%',
                            }}
                            onClick={() => {saveRule(false)}}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>)}
            </Box>
        </>
    );
};
